/**
 * ── Candidate email history (the ✉️ chip) ────────────────────────────────────
 *
 * One call, made only when the chip is clicked: every email with one candidate from the board
 * owner's OWN Gmail plus the CCA Log's automatic sends. Nothing here is part of the board payload.
 * Lives next to taCandidateNotesApi.ts for the same reason that one does — taActionBoardApi is at
 * its line budget.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';
const BASE = `${PROXY_URL}/api/ta-action-board`;

export interface CandidateEmailMessage {
  id: string;
  threadId: string | null;
  source: 'gmail' | 'log';
  /** Sent by interview.scheduling@ on its own — booking confirmations, reschedule and cancel notices. */
  automatic: boolean;
  kind?: string | null;
  from: { name: string | null; email: string | null };
  to: string[];
  cc: string[];
  date: string | null;
  subject: string | null;
  /** Plain text, quoted history stripped. Rendered as TEXT, never markup. */
  body: string | null;
}

export interface CandidateEmailThread {
  id: string;
  source: 'gmail' | 'log';
  threadId: string | null;
  subject: string | null;
  gmailUrl: string | null;
  latestAt: string | null;
  messageCount: number;
  /** Newest first. */
  messages: CandidateEmailMessage[];
}

/** Why the mailbox half is missing, when it is. `needs-connect` / `reconnect` → the Connect Gmail flow. */
export type GmailHalfCode = 'needs-connect' | 'reconnect' | 'mismatch' | 'lever' | 'no-address' | 'error';

export interface CandidateEmailHistory {
  opportunityId: string;
  addresses: string[];
  threads: CandidateEmailThread[];
  gmail: { ok: true; truncated?: boolean; failedThreads?: number } | { ok: false; code: GmailHalfCode; error: string };
  log: { ok: boolean; error?: string };
}

/** A deliberate refusal (403) — shown as a sentence, never retried. */
export class HistoryRefusedError extends Error {}

/** The viewer is not the owner of the board they are looking at. */
export class NotBoardOwnerError extends HistoryRefusedError {
  constructor() {
    super('Only the board owner can see their email history');
    this.name = 'NotBoardOwnerError';
  }
}

/** The candidate is not on the caller's board (e.g. a card left over from a board switch). */
export class NotOnBoardError extends HistoryRefusedError {
  constructor() {
    super('This candidate is not on your board');
    this.name = 'NotOnBoardError';
  }
}

const threadsBucket = (n: number) => (n === 0 ? '0' : n <= 3 ? '1_3' : n <= 10 ? '4_10' : 'over_10');

/**
 * @param gmailToken the board-wide Gmail grant's access token, or null — the proxy then returns the
 *   Log half and a `needs-connect` code rather than failing.
 */
export async function fetchCandidateEmails(input: {
  opportunityId: string;
  boardEmail: string;
  gmailToken: string | null;
}): Promise<CandidateEmailHistory> {
  const qs = new URLSearchParams({ opportunityId: input.opportunityId, board: input.boardEmail });
  const headers: Record<string, string> = {};
  if (input.gmailToken) headers['X-Gmail-Token'] = input.gmailToken;
  try {
    const res = await authenticatedFetch(`${BASE}/candidate-emails?${qs}`, {
      headers,
      // A colleague's board is an expected answer, not an error to report.
      suppressErrorStatuses: [403],
    });
    if (res.status === 403) {
      const body = (await res.json().catch(() => ({}))) as { code?: string; error?: string };
      if (body.code === 'not-board-owner') {
        void logAnalyticsEvent('candidate_emails_opened', { result: 'not_owner' });
        throw new NotBoardOwnerError();
      }
      if (body.code === 'not-on-board') {
        void logAnalyticsEvent('candidate_emails_opened', { result: 'not_on_board' });
        throw new NotOnBoardError();
      }
      throw new Error(body.error || 'Not allowed');
    }
    const data = (await res.json()) as CandidateEmailHistory;
    void logAnalyticsEvent('candidate_emails_opened', {
      result: 'ok',
      gmail: data.gmail.ok ? 'ok' : data.gmail.code,
      threads_bucket: threadsBucket(data.threads.length),
    });
    return data;
  } catch (error) {
    if (!(error instanceof HistoryRefusedError)) {
      void logAnalyticsEvent('candidate_emails_opened', { result: 'error' });
    }
    throw error;
  }
}
